import React from 'react'
import styled, { css } from 'styled-components'
import { mergeClassnames } from '@mote/helpers/dom'
import { SliderProvider, useSlider } from './slider.context'
import { useSliderFunctions } from './useSliderFunctions'

/**
 * Modified Chakra UI Slider component.
 * It provides context and functionality for all slider components
 *
 * @see Docs     https://chakra-ui.com/docs/form/slider
 * @see Source   https://github.com/chakra-ui/chakra-ui/blob/main/packages/slider/src/slider.tsx
 * @see WAI-ARIA https://www.w3.org/TR/wai-aria-practices/#slider
 */
export const Slider = React.forwardRef((props, ref) => {
  const { getInputProps, getRootProps, ...context } = useSliderFunctions(props)

  const rootProps = getRootProps()
  const inputProps = getInputProps({}, ref)

  return (
    <SliderProvider value={context}>
      <Root
        {...rootProps}
        className={mergeClassnames('range-slider', props.className)}
      >
        {props.children}
        <input {...inputProps} />
      </Root>
    </SliderProvider>
  )
})

export const SliderThumb = React.forwardRef((props, ref) => {
  const { getThumbProps } = useSlider()
  const thumbProps = getThumbProps(props, ref)

  return (
    <ThumbRoot
      {...thumbProps}
      className={mergeClassnames('range-slider__thumb', props.className)}
    />
  )
})

export const SliderTrack = React.forwardRef((props, ref) => {
  const { getTrackProps } = useSlider()
  const trackProps = getTrackProps(props, ref)

  return (
    <TrackRoot
      {...trackProps}
      className={mergeClassnames('range-slider__track', props.className)}
    />
  )
})

export const SliderFilledTrack = React.forwardRef((props, ref) => {
  const { getInnerTrackProps } = useSlider()
  const trackProps = getInnerTrackProps(props, ref)

  return (
    <TrackFilledRoot
      {...trackProps}
      className={mergeClassnames('range-slider__filled-track', props.className)}
    />
  )
})

export const SliderMark = React.forwardRef((props, ref) => {
  const { getMarkerProps } = useSlider()
  const markProps = getMarkerProps(props, ref)

  return (
    <div
      {...markProps}
      className={mergeClassnames('range-slider__marker', props.className)}
    />
  )
})

Slider.Thumb = SliderThumb
Slider.Track = SliderTrack
Slider.TrackFilled = SliderFilledTrack
Slider.Mark = SliderMark

const Root = styled.div`
  display: inline-block;
  position: relative;
  cursor: pointer;

  &:disabled,
  &[data-disabled] {
    opacity: 0.6;
    cursor: default;
    pointer-events: none;
  }

  ${({ isDisabled }) =>
    isDisabled &&
    css`
      opacity: 0.6;
      cursor: default;
      pointer-events: none;
    `};

  ${({ orientation }) => {
    switch (orientation) {
      case 'vertical':
        return css`
          height: 100%;
        `
      case 'horizontal':
        return css`
          width: 100%;
        `
    }
  }};
`

const TrackRoot = styled.div`
  overflow: hidden;
  background: #ececf3;

  &:disabled,
  &[data-disabled] {
    background: #d1d1e0;
  }

  ${({ isDisabled }) =>
    isDisabled &&
    css`
      background: #d1d1e0;
    `};

  ${({ size }) => {
    switch (size) {
      case 'sm':
        return css`
          border-radius: 1px;
        `
      case 'md':
        return css`
          border-radius: 2px;
        `
      case 'lg':
        return css`
          border-radius: 3px;
        `
    }
  }};

  ${({ size, orientation }) => {
    if (orientation === 'vertical') {
      switch (size) {
        case 'sm':
          return css`
            width: 2px;
          `
        case 'md':
          return css`
            width: 4px;
          `
        case 'lg':
          return css`
            width: 6px;
          `
      }
    }

    if (orientation === 'horizontal') {
      switch (size) {
        case 'sm':
          return css`
            height: 2px;
          `
        case 'md':
          return css`
            height: 4px;
          `
        case 'lg':
          return css`
            height: 6px;
          `
      }
    }
  }};
`

const TrackFilledRoot = styled.div`
  width: inherit;
  height: inherit;
  background: #ac0ae7;
`

const ThumbRoot = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  outline: transparent solid 2px;
  outline-offset: 2px;
  z-index: 1;
  border-radius: 100%;
  background: #ffffff;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06);
  border: 1px solid transparent;
  transition-property: transform, box-shadow;
  transition-duration: 150ms;

  &:focus,
  &[data-focus] {
    box-shadow: 0 0 0 3px rgba(172, 10, 231, 0.4);
  }

  &:disabled,
  &[data-disabled] {
    background: #d1d1e0;
  }

  ${({ isDisabled }) =>
    isDisabled &&
    css`
      background: #d1d1e0;
    `};

  ${({ size }) => {
    switch (size) {
      case 'sm':
        return css`
          width: 10px;
          height: 10px;
        `
      case 'md':
        return css`
          width: 14px;
          height: 14px;
        `
      case 'lg':
        return css`
          width: 16px;
          height: 16px;
        `
    }
  }};

  ${({ orientation }) => {
    switch (orientation) {
      case 'vertical':
        return css`
          left: 50%;
          transform: translateX(-50%);

          &:active,
          &[data-active] {
            transform: translateX(-50%) scale(1.15);
          }
        `

      case 'horizontal':
        return css`
          top: 50%;
          transform: translateY(-50%);

          &:active,
          &[data-active] {
            transform: translateY(-50%) scale(1.15);
          }
        `
    }
  }};
`
